import { CourseMaterial } from "routes/home/my-courses/index/coursesLoader.ts";
import {
  Kind,
  CompositeAsset,
  License,
  ManageAsset,
  StoreAsset,
} from "../../api/decimal/common-types/manageAssets.ts";
import { SystemKind } from "../../api/decimal/common-types/kind.ts";

interface FlexibleAsset {
  kind: Kind | SystemKind;
}

function getLowercaseKind(asset: FlexibleAsset): Lowercase<SystemKind> {
  return asset.kind.toLowerCase() as Lowercase<SystemKind>;
}

/**
 * This mostly means isEbook and is in contrast to things like access codes only,
 * but some print items have kind = "book" too so it's not so reliable in that context.
 * @param asset
 */
export function isBook(asset: FlexibleAsset): boolean {
  const kind = getLowercaseKind(asset);
  const nonBookKinds = [
    "subscription",
    "online resource",
    "package",
    "print book",
  ];
  return !nonBookKinds.includes(kind);
}

export function canBeOpenedInReader(
  asset: ManageAsset,
  license?: License
): boolean {
  if (!license) {
    return false;
  }

  return isBook(asset) && !license.expired;
}

export function isCourseware(asset: FlexibleAsset): boolean {
  const kind = getLowercaseKind(asset);

  return kind === "online resource" || kind === "online resource bundle";
}

/**
 * This requires the full ManageAsset because showing courseware details
 * means opening the dialog, which has a bunch of other dependencies
 */
export function canShowCoursewareDetails(
  asset: ManageAsset,
  license?: License
): boolean {
  return (
    isCourseware(asset) && !!license?.accessCode && !license.accessCode.ssoOnly
  );
}

export function canLaunchCourseware(
  asset: ManageAsset,
  license?: License
): boolean {
  return !!(
    isCourseware(asset) &&
    license?.accessCode?.href &&
    license?.accessCode?.revealed
  );
}

export function isExpiringSoon(license: License): boolean {
  const expirationDate = new Date(license.expiresAt);
  const now = new Date();

  // If it's expired, it's not expiring soon
  if (expirationDate <= now) {
    return false;
  }
  return (
    license.intendedUse === "vst store" && getDaysUntilExpiration(license) < 5
  );
}

/**
 * @see getHoursUntilExpiration
 *
 * Refactoring opportunity:
 * @see getRemainingTime
 */
export function getDaysUntilExpiration(license: License) {
  const expirationDate = new Date(license.expiresAt);
  const now = new Date();

  if (expirationDate <= now) {
    return -1;
  }

  return Math.floor(
    (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );
}

/**
 * @see getDaysUntilExpiration
 *
 * Refactoring opportunity:
 * @see getRemainingTime
 */
export function getHoursUntilExpiration(license: License) {
  const expirationDate = new Date(license.expiresAt);
  const now = new Date();

  if (expirationDate <= now) {
    return -1;
  }

  return Math.floor(
    (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60)
  );
}

export function isManageAsset(
  asset: ManageAsset | VST.Bridge.Asset | CourseMaterial
): asset is ManageAsset {
  return (asset as ManageAsset).ancillaries !== undefined;
}

export function isExploreAsset(
  asset: ManageAsset | VST.Bridge.Asset | CourseMaterial | StoreAsset
): asset is VST.Bridge.Asset {
  return (asset as VST.Bridge.Asset).active !== undefined;
}

export function isCourseMaterialAsset(
  asset: ManageAsset | VST.Bridge.Asset | CourseMaterial
): asset is VST.Bridge.Asset {
  return (asset as CourseMaterial).optOut !== undefined;
}

export function isStoreAsset(
  asset: CompositeAsset<"explore"> | StoreAsset
): asset is StoreAsset {
  return (asset as StoreAsset).store?.slug !== undefined;
}
